/**
 * Prefetch helpers — warm the Service Worker cache the instant the
 * user's finger is over a tap target, so navigation feels instantaneous.
 *
 * Everything here is a fire-and-forget `fetch()` — the Service Worker
 * (public/sw.js) catches the response and stores it in Cache Storage
 * so the real navigation reuses those bytes 0-network.
 *
 * `pointerdown` fires ~50-150ms before `click` on both mobile taps
 * and desktop clicks, so this window is enough to fully load a
 * /api/recordings/{id} payload before React Router mounts the new page.
 */

const API = process.env.REACT_APP_BACKEND_URL;
const seen = new Set(); // avoid spamming the same URL within a session

const _pf = (url) => {
  if (!url || seen.has(url)) return;
  seen.add(url);
  // credentials: 'include' so cookies flow (login for admin endpoints).
  fetch(url, { credentials: 'include', priority: 'low' }).catch(() => {});
};

/** Prefetch a recording's full metadata blob AND warm the audio stream
 *  cache. The audio ping is a 1-byte Range GET to `/api/stream/{fid}`
 *  which forces the backend to check S3 / kick off a Drive fetch so by
 *  the time the user presses Play the file is already in the on-disk
 *  cache. Feb-2026: dramatically reduces cold-play latency on cellular.
 *  A 1-byte body over gzipped HTTP costs almost nothing. */
export const prefetchRecording = (driveFileId) => {
  if (!driveFileId) return;
  _pf(`${API}/api/recordings/${encodeURIComponent(driveFileId)}`);
  const streamUrl = `${API}/api/stream/${encodeURIComponent(driveFileId)}`;
  const warmKey = streamUrl + ':warm';
  if (!seen.has(warmKey)) {
    seen.add(warmKey);
    fetch(streamUrl, {
      method: 'GET',
      credentials: 'omit',
      priority: 'low',
      headers: { Range: 'bytes=0-0' },
    }).catch(() => {});
  }
};

/** Prefetch everything the speaker page needs to render immediately.
 *  Feb-2026 speed pass: added the folder-list + guest-groups fetches
 *  so a hover→click on any speaker tile arrives at the fully-warmed
 *  page instead of watching a spinner while 5 sequential requests
 *  fire. All are fire-and-forget (fetch dedupe handles overlap with
 *  the SpeakerPage's own subsequent calls). */
export const prefetchSpeaker = (speakerName) => {
  if (!speakerName) return;
  const enc = encodeURIComponent(speakerName);
  _pf(`${API}/api/speaker-image?name=${enc}`);
  _pf(`${API}/api/speakers/${enc}/folder-order`);
  _pf(`${API}/api/speakers/${enc}/cross-media`);
  _pf(`${API}/api/speaker-folders?name=${enc}`);
  _pf(`${API}/api/speaker-guest-groups?name=${enc}`);
};

/** Prefetch a topic page's recordings. */
export const prefetchTopic = (slug) => {
  if (!slug) return;
  _pf(`${API}/api/recordings?topic=${encodeURIComponent(slug)}&limit=50`);
};

/** React-ready handler pack: spread onto any Link/Button that opens a
 *  new page. Fires on pointerdown (touch AND mouse) + focus keyboard
 *  navigation. Idempotent per URL per session. */
export const prefetchHandlers = (fn, arg) => ({
  onPointerEnter: () => fn(arg),
  onPointerDown:  () => fn(arg),
  onFocus:        () => fn(arg),
  onTouchStart:   () => fn(arg),
});
